/**
 * 插件工具栏：从 `plugin-store` 读取各插件注册的 toolbar 按钮，渲染主区域可见按钮与溢出菜单；
 * popup 与 modal 保持受控渲染，drawer 交给统一的命令式 Sheet 管理。
 */
import { MoreHorizontal } from "lucide-react";
import { useState, useMemo } from "react";

import { useSheet } from "@/shared/components/sheet";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";

import { PluginDrawer } from "./plugin-drawer";
import { PluginIcon } from "./plugin-icon";
import { usePluginManager } from "./plugin-manager-context";
import { PluginModal } from "./plugin-modal";
import { PluginPopup } from "./plugin-popup";
import { getPluginDrawerSheetKey } from "../lib/plugin-panel-key";
import { usePluginStore, type ToolbarButtonState } from "../state/plugin-store";


const MAX_VISIBLE_BUTTONS = 3;

export function PluginToolbar() {
  const { open: openSheet } = useSheet();
  const toolbarButtons = usePluginStore((s) => s.toolbarButtons);
  const [openPluginId, setOpenPluginId] = useState<string | null>(null);
  const manager = usePluginManager();

  const visibleButtons = useMemo(() => {
    const all = Array.from(toolbarButtons.values()).filter((b) => b.visible);
    return all;
  }, [toolbarButtons]);

  const directButtons = visibleButtons.slice(0, MAX_VISIBLE_BUTTONS);
  const overflowButtons = visibleButtons.slice(MAX_VISIBLE_BUTTONS);

  if (visibleButtons.length === 0) return null;

  const resolvePluginSrc = (btn: ToolbarButtonState) => {
    if (!manager) return "";
    const baseUrl = manager.getBaseUrl(btn.pluginId);
    if (!baseUrl) return "";
    return manager.resolveUrl(baseUrl, btn.config.path);
  };

  const renderContainer = (btn: ToolbarButtonState) => {
    const src = resolvePluginSrc(btn);
    const isOpen = openPluginId === btn.pluginId;
    const onClose = () => setOpenPluginId(null);

    switch (btn.config.display) {
      case "modal":
        return (
          <PluginModal
            key={btn.pluginId}
            open={isOpen}
            onOpenChange={(open) => !open && onClose()}
            pluginId={btn.pluginId}
            src={src}
            title={btn.title}
            width={btn.config.width}
            height={btn.config.height}
          />
        );
      default:
        return null;
    }
  };

  const handleOpen = (btn: ToolbarButtonState) => {
    if (btn.config.display !== "drawer") {
      setOpenPluginId(btn.pluginId);
      return;
    }

    const src = resolvePluginSrc(btn);
    if (!src) {
      return;
    }

    void openSheet(PluginDrawer, {
      pluginId: btn.pluginId,
      src,
      title: btn.title,
    }, {
      key: getPluginDrawerSheetKey(btn.pluginId),
      className: "flex flex-col overflow-hidden p-0",
      style: { width: btn.config.width ?? 360, maxWidth: "100vw" },
    });
  };

  return (
    <>
      {directButtons.map((btn) => {
        if (btn.config.display === "popup") {
          const src = resolvePluginSrc(btn);
          return (
            <PluginPopup
              key={btn.pluginId}
              open={openPluginId === btn.pluginId}
              onOpenChange={(open) => setOpenPluginId(open ? btn.pluginId : null)}
              pluginId={btn.pluginId}
              src={src}
              width={btn.config.width}
              height={btn.config.height}
            >
              <ToolbarButton btn={btn} onClick={() => handleOpen(btn)} />
            </PluginPopup>
          );
        }

        return (
          <span key={btn.pluginId}>
            <ToolbarButton btn={btn} onClick={() => handleOpen(btn)} />
            {renderContainer(btn)}
          </span>
        );
      })}

      {overflowButtons.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {overflowButtons.map((btn) => (
              <DropdownMenuItem
                key={btn.pluginId}
                onClick={() => handleOpen(btn)}
              >
                <PluginIcon name={btn.icon} className="mr-2 h-4 w-4" />
                {btn.title}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {overflowButtons.map((btn) => renderContainer(btn))}
    </>
  );
}

/** 单个工具栏按钮：带 Tooltip 与可选角标，点击时由父级切换对应插件浮层 */
function ToolbarButton({
  btn,
  onClick,
}: {
  btn: ToolbarButtonState;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 relative" onClick={onClick}>
          <PluginIcon name={btn.icon} className="h-4 w-4" />
          <span className="text-sm">{btn.title}</span>
          {btn.badge && (
            <Badge
              variant={btn.badge.variant ?? "default"}
              className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[10px] leading-none"
            >
              {btn.badge.text}
            </Badge>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{btn.title}</TooltipContent>
    </Tooltip>
  );
}
